/**
 * Lane C0-PROPS: the plains kit (render/kit/) joins each camera angle's scenery. As a map's scenery is built this
 * switches the building kit on for the 256 m plains only (`setKitMap`; scenery.ts / layoutLook.ts then draw its
 * structures as themselves) and returns the kit's props placed per the layout (kit/placement.ts). Draws nothing per
 * frame. Off with `localStorage['c0.kit'] = '0'`.
 */
import { Tile, tileAt, toScreen } from '@nine/shared';
import { setKitMap, kitMap } from '../kit';
import { PLAINS_DRESSING } from '../kit/dressing';
import '../kit/lights';
import { PLAINS_PROPS } from '../kit/placement';
import { animStill, propWatcher } from '../kit/propAnimPass';
import type { PropId } from '../kit/props';
import { masterLookImg } from '../masterLook';
import { truePxImg } from '../truepx/page';
import type { Prop } from '../terrain';
import { registerRenderPass } from './registry';

registerRenderPass({
  id: 'kitProps',
  layer: 'ground',
  order: 510,
  scenery(v) {
    setKitMap(v.map);
    if (!kitMap(v.map)) return [];
    // A quarter turn swaps which way a fence runs on screen.
    const [ox, oy] = v.view.turn(0, 0);
    const [ex] = v.view.turn(1, 0);
    const swap = Math.abs(ex - ox) < 0.5;
    void oy;
    const props: Prop[] = [];
    // Props that move (kit/propAnim*.ts): made from their loop's first frame, then watched by its pass.
    const watch = propWatcher(v.ground);
    const turnId = (id: PropId): PropId => {
      if (!swap) return id;
      const m = /^(.*)([XY])$/.exec(id);
      if (!m) return id;
      const other = `${m[1]}${m[2] === 'X' ? 'Y' : 'X'}` as PropId;
      return other;
    };
    const place = (pid: PropId, x: number, y: number) => {
      const sp = animStill(turnId(pid));
      const [vx, vy] = v.view.turn(x, y);
      const [sx, sy] = toScreen(vx, vy, v.origin);
      // Flat decals (carpets) sort at their far corner so every figure and prop on them draws after them.
      const flat = (sp as { flat?: readonly [number, number] }).flat;
      const d = flat ? vx + vy - (flat[0] + flat[1]) / 2 - 0.5 : vx + vy;
      props.push({ img: truePxImg(masterLookImg(sp.img)), x: Math.round(sx - sp.ax), y: Math.round(sy - sp.ay), d });
      watch(props[props.length - 1]!, turnId(pid), x, y);
    };
    for (const p of PLAINS_PROPS) place(p.id, p.at[0], p.at[1]);
    // The dressing (kit/dressing.ts): land pieces only on open ground, water pieces only on water.
    for (const p of PLAINS_DRESSING) {
      const [vx, vy] = v.view.turn(p.at[0], p.at[1]);
      const t = tileAt(v.map, Math.floor(vx), Math.floor(vy));
      if (p.water ? t !== Tile.Water : t === Tile.Water || t === Tile.Building || t === Tile.Tree || t === Tile.Wall || t === Tile.Rock) continue;
      place(p.id, p.at[0], p.at[1]);
    }
    return props;
  },
  draw() {},
});
